// Transposition, enharmonic spelling and capo help for the chord chart. Pure, no DOM.
// Chord labels stay sharp-canonical everywhere (the server's vocabulary, and what chordShape() reads);
// ♭ spelling is applied only when a label is drawn or exported.

import { NOTE_NAMES, chordShape } from "./chords";

const FLAT_NAMES = ["C", "Db", "D", "Eb", "E", "F", "Gb", "G", "Ab", "A", "Bb", "B"];

export const TRANSPOSE_MIN = -6;
export const TRANSPOSE_MAX = 6;
export const CAPO_MAX = 7;
const CAPO_SUGGEST_MAX = 5;   // capos above the 5th fret are rarely used, so never suggest them

export type KeyQuality = "major" | "minor";
export interface KeySetting { transpose: number; capo: number }

const mod12 = (x: number) => ((x % 12) + 12) % 12;

/** pitch class of a note name, sharp or flat spelling ("C#" and "Db" → 1); -1 if unknown */
export function pitchClass(name: string): number {
  const i = NOTE_NAMES.indexOf(name);
  return i >= 0 ? i : FLAT_NAMES.indexOf(name);
}

function parse(label: string) {
  const [main, bass] = label.split("/");
  const m = /^([A-G][#b]?)(.*)$/.exec(main);
  if (!m || pitchClass(m[1]) < 0) return null;
  const b = bass ? /^([A-G][#b]?)$/.exec(bass) : null;
  return { root: pitchClass(m[1]), quality: m[2], bass: b ? pitchClass(b[1]) : null };
}

/** shift a chord by `semis` semitones; the result is sharp-canonical ("Bbm7" +1 → "Bm7"); unknown labels pass through */
export function transposeChord(label: string, semis: number): string {
  const p = parse(label);
  if (!p) return label;
  return NOTE_NAMES[mod12(p.root + semis)] + p.quality + (p.bass != null ? "/" + NOTE_NAMES[mod12(p.bass + semis)] : "");
}

/** keys written with flats: F B♭ E♭ A♭ D♭ major · D G C F B♭ E♭ minor (the tritone key stays F♯) */
export function keyUsesFlats(tonicPc: number, quality: KeyQuality): boolean {
  return (quality === "minor" ? [2, 7, 0, 5, 10, 3] : [5, 10, 3, 8, 1]).includes(tonicPc);
}

/** spell a chord for display/export: with flats, "A#m7/D#" → "Bbm7/Eb" */
export function spellChord(label: string, flats: boolean): string {
  const p = parse(label);
  if (!p) return label;
  const names = flats ? FLAT_NAMES : NOTE_NAMES;
  return names[p.root] + p.quality + (p.bass != null ? "/" + names[p.bass] : "");
}

/** the key a setting sounds in (capo does not change it), spelled for that key: A +1 → "Bb", Am +1 → "Bbm" */
export function soundingKey(key: { tonic: string; quality: KeyQuality }, transpose: number): string {
  const pc = pitchClass(key.tonic);
  if (pc < 0) return key.tonic;
  const t = mod12(pc + transpose);
  return (keyUsesFlats(t, key.quality) ? FLAT_NAMES : NOTE_NAMES)[t] + (key.quality === "minor" ? "m" : "");
}

/** how chords are drawn for a key setting: shapes move by (transpose − capo), spelled for the shape key */
export interface ChordView {
  shift: number;
  /** sharp-canonical label of the shape to play (feed chordShape) */
  shape: (label: string) => string;
  /** display/export spelling of that shape */
  name: (label: string) => string;
  /** display/export spelling of a label that is already a shape (e.g. a play-style voicing name) */
  spell: (shapeLabel: string) => string;
}
export function chordView(key: { tonic: string; quality: KeyQuality }, setting: KeySetting): ChordView {
  const shift = setting.transpose - setting.capo;
  const pc = pitchClass(key.tonic);
  const flats = pc >= 0 && keyUsesFlats(mod12(pc + shift), key.quality);
  return {
    shift,
    shape: (label) => transposeChord(label, shift),
    name: (label) => spellChord(transposeChord(label, shift), flats),
    spell: (shapeLabel) => spellChord(shapeLabel, flats),
  };
}

/** a barre shape: no open string and the lowest fret held on 2+ strings (the index finger lies across) — F, Bm, F#m7 … */
export function isBarre(frets: number[]): boolean {
  if (frets.includes(0)) return false;
  const fretted = frets.filter((f) => f > 0);
  if (!fretted.length) return false;
  const low = Math.min(...fretted);
  return fretted.filter((f) => f === low).length >= 2;
}

/** distinct chords of the song that need a barre after shifting by `shift` */
export function barreCount(chords: string[], shift: number): number {
  let n = 0;
  for (const ch of new Set(chords)) {
    const sh = chordShape(transposeChord(ch, shift));
    if (sh && isBarre(sh.frets)) n++;
  }
  return n;
}

export interface CapoPick { capo: number; barre: number; base: number }

/** the capo (frets 1–5) that leaves the fewest barre chords at this transposition; ties go to the lower fret.
 *  null when no capo beats playing without one. */
export function suggestCapo(chords: string[], transpose: number): CapoPick | null {
  const base = barreCount(chords, transpose);
  let best: CapoPick | null = null;
  for (let capo = 1; capo <= CAPO_SUGGEST_MAX; capo++) {
    const barre = barreCount(chords, transpose - capo);
    if (!best || barre < best.barre) best = { capo, barre, base };
  }
  return best && best.barre < base ? best : null;
}

export function clampSetting(s: Partial<KeySetting>): KeySetting {
  const int = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? Math.round(v) : 0);
  return {
    transpose: Math.max(TRANSPOSE_MIN, Math.min(TRANSPOSE_MAX, int(s.transpose))),
    capo: Math.max(0, Math.min(CAPO_MAX, int(s.capo))),
  };
}
